import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import {
  X,
  Send,
  Bot,
  User,
  Loader2,
  Sparkles,
  Trash2,
  RotateCcw,
  ArrowRight,
  ArrowLeft,
  MessageSquare,
  ClipboardList,
  Printer,
  Palette,
  DollarSign,
  PenTool,
  FileText,
  Check,
  ImageOff,
  ExternalLink,
  Layers,
  Droplets,
  Sun,
  CheckCircle,
  Trophy,
  AlertCircle,
  Thermometer,
  Activity,
  Zap as ZapIcon,
  MoveHorizontal,
  Search,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Recommendation Card ────────────────────────────────────────
const RecommendationCard = ({ data }) => {
  const isTopPick = data.isTopPick;
  return (
    <div
      className={`relative bg-white rounded-2xl shadow-sm border overflow-hidden hover:shadow-xl transition-all duration-300 group flex flex-col h-full ${
        isTopPick ? "border-[#F76B1C] ring-1 ring-[#F76B1C]" : "border-gray-200"
      }`}
    >
      <div className="h-48 w-full overflow-hidden bg-gray-100 relative flex items-center justify-center">
        {data.imageUrl ? (
          <img
            src={data.imageUrl}
            alt={data.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div className="text-gray-300 flex flex-col items-center gap-1">
            <ImageOff size={32} />
          </div>
        )}
        {isTopPick && (
          <div className="absolute top-3 right-3 bg-[#F76B1C] text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg z-10">
            <Trophy size={12} className="text-yellow-300" /> TOP PICK
          </div>
        )}
      </div>
      <div className="p-5 flex-grow flex flex-col">
        <div className="flex justify-between items-start mb-3">
          <div>
            <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-bold rounded-full mb-1.5 uppercase tracking-wide">
              {data.brand}
            </span>
            <h3 className="text-lg font-bold text-gray-900 leading-tight">{data.name}</h3>
            <p className="text-gray-500 text-sm mt-0.5">{data.material}</p>
          </div>
          {data.priceEstimate && (
            <span className="text-sm font-bold text-gray-900 shrink-0 ml-2">{data.priceEstimate}</span>
          )}
        </div>
        <div className="flex items-start gap-2 mb-4">
          <CheckCircle className={`w-4 h-4 shrink-0 mt-0.5 ${isTopPick ? "text-[#F76B1C]" : "text-emerald-500"}`} />
          <p className="text-sm text-gray-600 leading-relaxed">{data.reason}</p>
        </div>
        {data.technicalSpecs && (
          <div className="mt-auto space-y-3">
            {(data.technicalSpecs.tensileStrength || data.technicalSpecs.hdt) && (
              <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Mechanical & Thermal</h4>
                <div className="grid grid-cols-2 gap-2">
                  {data.technicalSpecs.tensileStrength && (
                    <div className="flex items-start gap-1.5">
                      <Activity size={14} className="text-indigo-500 mt-0.5" />
                      <div><span className="block text-[9px] text-gray-400 font-semibold uppercase">Tensile</span><span className="text-xs font-semibold text-gray-700">{data.technicalSpecs.tensileStrength}</span></div>
                    </div>
                  )}
                  {data.technicalSpecs.elongation && (
                    <div className="flex items-start gap-1.5">
                      <MoveHorizontal size={14} className="text-violet-500 mt-0.5" />
                      <div><span className="block text-[9px] text-gray-400 font-semibold uppercase">Elongation</span><span className="text-xs font-semibold text-gray-700">{data.technicalSpecs.elongation}</span></div>
                    </div>
                  )}
                  {data.technicalSpecs.impactStrength && (
                    <div className="flex items-start gap-1.5">
                      <ZapIcon size={14} className="text-yellow-500 mt-0.5" />
                      <div><span className="block text-[9px] text-gray-400 font-semibold uppercase">Impact</span><span className="text-xs font-semibold text-gray-700">{data.technicalSpecs.impactStrength}</span></div>
                    </div>
                  )}
                  {data.technicalSpecs.hdt && (
                    <div className="flex items-start gap-1.5">
                      <Thermometer size={14} className="text-red-500 mt-0.5" />
                      <div><span className="block text-[9px] text-gray-400 font-semibold uppercase">HDT</span><span className="text-xs font-semibold text-gray-700">{data.technicalSpecs.hdt}</span></div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
              <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Processing</h4>
              <div className="grid grid-cols-3 gap-2">
                <div className="flex items-start gap-1.5">
                  <Layers size={14} className="text-blue-500 mt-0.5" />
                  <div><span className="block text-[9px] text-gray-400 font-semibold uppercase">Layer</span><span className="text-xs font-semibold text-gray-700">{data.technicalSpecs.layerHeight}</span></div>
                </div>
                <div className="flex items-start gap-1.5">
                  <Droplets size={14} className="text-cyan-500 mt-0.5" />
                  <div><span className="block text-[9px] text-gray-400 font-semibold uppercase">Wash</span><span className="text-xs font-semibold text-gray-700">{data.technicalSpecs.washTime}</span></div>
                </div>
                <div className="flex items-start gap-1.5">
                  <Sun size={14} className="text-orange-500 mt-0.5" />
                  <div><span className="block text-[9px] text-gray-400 font-semibold uppercase">Cure</span><span className="text-xs font-semibold text-gray-700">{data.technicalSpecs.cureTime}</span></div>
                </div>
              </div>
              {data.technicalSpecs.notes && (
                <div className="flex items-start gap-1.5 mt-2 pt-2 border-t border-gray-200">
                  <AlertCircle size={14} className="text-gray-400 mt-0.5" />
                  <span className="text-xs text-gray-600">{data.technicalSpecs.notes}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="p-4 pt-0">
        <a
          href={data.productUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="resin-card-link"
          className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-medium transition-all text-sm ${
            isTopPick
              ? "bg-[#F76B1C] hover:bg-[#E05A0F] text-white"
              : "bg-gray-900 hover:bg-gray-800 text-white"
          }`}
        >
          View on 3D Printer Gear <ExternalLink size={14} />
        </a>
      </div>
    </div>
  );
};

// ─── Option Button (Questionnaire) ─────────────────────────────
const OptionButton = ({ selected, onClick, label, desc, image }) => {
  const [imgError, setImgError] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-xl border transition-all duration-200 flex flex-col h-full overflow-hidden group relative ${
        selected
          ? "border-[#F76B1C] bg-orange-50 ring-1 ring-[#F76B1C] shadow-md"
          : "border-gray-200 bg-white hover:border-orange-200 hover:bg-gray-50"
      }`}
    >
      {image && (
        <div className="w-full h-28 overflow-hidden relative bg-gray-100 flex items-center justify-center">
          {!imgError ? (
            <img src={image} alt={label} onError={() => setImgError(true)} className="w-full h-full object-cover" />
          ) : (
            <div className="text-gray-300 flex flex-col items-center gap-1"><ImageOff size={20} /></div>
          )}
          {selected && (
            <div className="absolute top-2 right-2 bg-[#F76B1C] text-white p-1 rounded-full shadow-lg z-20">
              <Check size={12} strokeWidth={3} />
            </div>
          )}
        </div>
      )}
      <div className="p-3 flex flex-col gap-0.5 flex-grow">
        <span className={`font-semibold text-sm ${selected ? "text-[#F76B1C]" : "text-gray-900"}`}>{label}</span>
        {desc && <span className="text-xs text-gray-500 leading-tight">{desc}</span>}
      </div>
    </button>
  );
};

// ─── Chat View ──────────────────────────────────────────────────
const ChatView = () => {
  const [messages, setMessages] = useState([
    { id: "initial", role: "model", text: "Hello! I'm **Resin Genius** by 3D Printer Gear. Tell me what you're planning to print, and I'll help you find the perfect Formlabs material." },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const messagesEndRef = useRef(null);
  const sessionId = useRef(`rg-chat-${Date.now()}`);
  const requestId = useRef(0);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, isLoading]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    setShowConfirm(false);
    const userMsg = { id: `u-${Date.now()}`, role: "user", text: input };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsLoading(true);
    const currentReq = ++requestId.current;

    try {
      const history = messages.filter((m) => m.id !== "initial").map((m) => ({ role: m.role, text: m.text }));
      const res = await fetch(`${API_URL}/api/resin-genius/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, history, message: input }),
      });
      const data = await res.json();
      if (currentReq === requestId.current) {
        setMessages((prev) => [...prev, { id: `m-${Date.now()}`, role: "model", text: data.response || "Sorry, I couldn't process that." }]);
      }
    } catch {
      if (currentReq === requestId.current) {
        setMessages((prev) => [...prev, { id: `e-${Date.now()}`, role: "model", text: "Connection error. Please try again." }]);
      }
    } finally {
      if (currentReq === requestId.current) setIsLoading(false);
    }
  };

  const handleClear = () => {
    if (showConfirm) {
      requestId.current++;
      setMessages([{ id: "initial", role: "model", text: "Hello! I'm **Resin Genius** by 3D Printer Gear. Tell me what you're planning to print, and I'll help you find the perfect Formlabs material." }]);
      setInput("");
      setIsLoading(false);
      setShowConfirm(false);
      sessionId.current = `rg-chat-${Date.now()}`;
    } else {
      setShowConfirm(true);
      setTimeout(() => setShowConfirm(false), 3000);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="font-semibold text-gray-700 text-sm">Conversational Mode</span>
        </div>
        <button
          onClick={handleClear}
          data-testid="resin-chat-clear"
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-all text-xs font-medium border ${
            showConfirm
              ? "bg-red-500 text-white border-red-600"
              : "text-gray-500 hover:text-red-500 hover:bg-red-50 border-transparent"
          }`}
        >
          {showConfirm ? <><RotateCcw size={14} /> Confirm</> : <><Trash2 size={14} /> Clear</>}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50">
        {messages.map((msg) => (
          <div key={msg.id} className={`flex items-start gap-2.5 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${msg.role === "user" ? "bg-gray-900 text-white" : "bg-[#F76B1C] text-white"}`}>
              {msg.role === "user" ? <User size={14} /> : <Bot size={14} />}
            </div>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
              msg.role === "user"
                ? "bg-gray-900 text-white rounded-tr-none"
                : "bg-white border border-gray-100 text-gray-700 rounded-tl-none"
            }`}>
              <ReactMarkdown
                components={{
                  a: (props) => <a {...props} className="text-[#F76B1C] font-medium underline hover:text-[#E05A0F]" target="_blank" rel="noopener noreferrer" />,
                  ul: (props) => <ul {...props} className="list-disc pl-4 my-1.5 space-y-0.5" />,
                  ol: (props) => <ol {...props} className="list-decimal pl-4 my-1.5 space-y-0.5" />,
                  strong: (props) => <strong {...props} className="font-bold text-gray-900" />,
                  p: (props) => <p {...props} className="mb-1.5 last:mb-0" />,
                }}
              >
                {msg.text}
              </ReactMarkdown>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex items-start gap-2.5">
            <div className="w-8 h-8 rounded-full bg-[#F76B1C] text-white flex items-center justify-center shrink-0"><Bot size={14} /></div>
            <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-none px-4 py-2.5 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-[#F76B1C]" />
              <span className="text-gray-400 text-sm">Consulting the catalog...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} className="h-2" />
      </div>

      <div className="p-3 bg-white border-t border-gray-200">
        <div className="relative flex items-center gap-2 max-w-3xl mx-auto">
          <input
            data-testid="resin-chat-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            placeholder="What are you printing today?"
            className="w-full bg-gray-50 text-gray-900 placeholder-gray-400 border border-gray-200 rounded-full py-3 pl-5 pr-12 focus:outline-none focus:ring-2 focus:ring-[#F76B1C]/20 focus:border-[#F76B1C] transition-all text-sm"
          />
          <button
            data-testid="resin-chat-send"
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 bg-[#F76B1C] hover:bg-[#E05A0F] disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-full flex items-center justify-center transition-all"
          >
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Questionnaire View ─────────────────────────────────────────
const QuestionnaireView = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [formData, setFormData] = useState({
    application: "",
    printerType: "form_4_series",
    experienceLevel: "beginner",
    aesthetic: "standard",
    budget: "standard",
    specialRequirements: "",
  });

  const materialOptions = [
    { id: "standard", label: "Standard", desc: "General purpose, matte finish", image: "/images/resin-genius/finishes/Standard.jpeg" },
    { id: "tough", label: "Tough & Durable", desc: "High impact, engineering", image: "/images/resin-genius/finishes/ToughDurable.jpeg" },
    { id: "flexible", label: "Flexible", desc: "Rubber-like, elastic", image: "/images/resin-genius/finishes/Flexible.jpeg" },
    { id: "rigid", label: "Rigid", desc: "Glass/Ceramic filled", image: "/images/resin-genius/finishes/Rigid.jpeg" },
    { id: "specialty", label: "Specialty", desc: "High temp, Medical, ESD", image: "/images/resin-genius/finishes/Specialty.jpeg" },
  ];

  const handleSubmit = async () => {
    if (!formData.application.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/resin-genius/recommend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: `rg-q-${Date.now()}`, ...formData }),
      });
      const data = await res.json();
      if (data.recommendations?.length) setResults(data.recommendations);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] text-center px-4">
        <Loader2 size={48} className="text-[#F76B1C] animate-spin" />
        <h3 className="mt-6 text-xl font-bold text-gray-900">Analyzing Requirements</h3>
        <p className="mt-2 text-gray-500 text-sm">Searching the Formlabs Resin database...</p>
      </div>
    );
  }

  if (results.length > 0) {
    return (
      <div className="p-4 overflow-y-auto h-full">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Your Recommendations</h2>
          <button
            onClick={() => setResults([])}
            data-testid="resin-edit-search"
            className="text-[#F76B1C] font-bold text-sm flex items-center gap-1 px-3 py-1.5 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors"
          >
            <RotateCcw size={14} /> Edit Search
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {results.map((rec, idx) => (
            <RecommendationCard key={idx} data={rec} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 overflow-y-auto h-full space-y-8">
      <section>
        <div className="flex items-center gap-2 mb-3">
          <div className="p-2 bg-blue-50 text-blue-600 rounded-lg"><PenTool size={18} /></div>
          <h3 className="text-base font-bold text-gray-900">1. What are you printing?</h3>
        </div>
        <textarea
          data-testid="resin-quest-application"
          value={formData.application}
          onChange={(e) => setFormData({ ...formData, application: e.target.value })}
          placeholder="e.g. A functional gear, a dental aligner model, or a detailed figurine..."
          className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#F76B1C]/20 focus:border-[#F76B1C] outline-none min-h-[80px] resize-none text-gray-900 placeholder-gray-400 text-sm"
        />
      </section>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <div className="p-2 bg-purple-50 text-purple-600 rounded-lg"><Printer size={18} /></div>
          <h3 className="text-base font-bold text-gray-900">2. Printer</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <OptionButton selected={formData.printerType === "form_3_series"} onClick={() => setFormData({ ...formData, printerType: "form_3_series" })} label="Form 3 / 3B / 3L" desc="LFS printers" image="/images/resin-genius/printers/form-3.jpg" />
          <OptionButton selected={formData.printerType === "form_4_series"} onClick={() => setFormData({ ...formData, printerType: "form_4_series" })} label="Form 4 / 4B / 4L" desc="Next-gen LFD printers" image="/images/resin-genius/printers/form-4.jpg" />
          <OptionButton selected={formData.printerType === "other_sla"} onClick={() => setFormData({ ...formData, printerType: "other_sla" })} label="Other SLA / DLP" desc="Third party printers" image="/images/resin-genius/printers/GeneralSLA.jpg" />
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <div className="p-2 bg-pink-50 text-pink-600 rounded-lg"><Palette size={18} /></div>
          <h3 className="text-base font-bold text-gray-900">3. Material Class</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {materialOptions.map((opt) => (
            <OptionButton key={opt.id} selected={formData.aesthetic === opt.id} onClick={() => setFormData({ ...formData, aesthetic: opt.id })} label={opt.label} desc={opt.desc} image={opt.image} />
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <div className="p-2 bg-amber-50 text-amber-600 rounded-lg"><DollarSign size={18} /></div>
          <h3 className="text-base font-bold text-gray-900">4. Preferences</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Experience Level</label>
            <div className="flex flex-col gap-1.5">
              {[
                { id: "beginner", label: "Beginner", time: "< 6 months" },
                { id: "intermediate", label: "Intermediate", time: "6m - 2 years" },
                { id: "expert", label: "Expert", time: "2+ years" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, experienceLevel: opt.id })}
                  className={`px-3 py-2.5 rounded-xl border text-left transition-all text-sm ${
                    formData.experienceLevel === opt.id
                      ? "border-[#F76B1C] bg-orange-50 ring-1 ring-[#F76B1C]"
                      : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span className={`font-semibold ${formData.experienceLevel === opt.id ? "text-[#F76B1C]" : "text-gray-700"}`}>{opt.label}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${formData.experienceLevel === opt.id ? "bg-orange-100 text-[#F76B1C]" : "bg-gray-100 text-gray-500"}`}>{opt.time}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Budget</label>
            <div className="flex flex-col gap-1.5">
              {[
                { id: "standard", label: "Standard", range: "$$" },
                { id: "professional", label: "Professional", range: "$$$" },
                { id: "industrial", label: "Industrial / Medical", range: "$$$$" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, budget: opt.id })}
                  className={`px-3 py-2.5 rounded-xl border text-left transition-all text-sm ${
                    formData.budget === opt.id
                      ? "border-[#F76B1C] bg-orange-50 ring-1 ring-[#F76B1C]"
                      : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span className={`font-semibold ${formData.budget === opt.id ? "text-[#F76B1C]" : "text-gray-700"}`}>{opt.label}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${formData.budget === opt.id ? "bg-orange-100 text-[#F76B1C]" : "bg-gray-100 text-gray-500"}`}>{opt.range}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"><FileText size={18} /></div>
          <h3 className="text-base font-bold text-gray-900">5. Special Requirements (Optional)</h3>
        </div>
        <textarea
          value={formData.specialRequirements}
          onChange={(e) => setFormData({ ...formData, specialRequirements: e.target.value })}
          placeholder="e.g. Must be food safe, high temperature resistance..."
          className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#F76B1C]/20 focus:border-[#F76B1C] outline-none min-h-[60px] resize-none text-gray-900 placeholder-gray-400 text-sm"
        />
      </section>

      <div className="sticky bottom-0 bg-white pt-3 pb-1 border-t border-gray-200">
        <button
          data-testid="resin-quest-submit"
          onClick={handleSubmit}
          disabled={!formData.application.trim()}
          className="w-full py-3.5 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2"
        >
          Find My Resin <Sparkles size={18} className={formData.application.trim() ? "text-[#F76B1C]" : ""} />
        </button>
      </div>
    </div>
  );
};

// ─── Main Modal ─────────────────────────────────────────────────
export const ResinGeniusModal = ({ isOpen, onClose }) => {
  const [view, setView] = useState("home"); // home | chat | questionnaire

  const handleClose = useCallback(() => {
    setView("home");
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative w-[95vw] max-w-5xl h-[90vh] max-h-[750px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-[#0A0A0A] text-white shrink-0">
          <div className="flex items-center gap-3">
            {view !== "home" && (
              <button
                onClick={() => setView("home")}
                data-testid="resin-modal-back"
                className="flex items-center gap-1 text-white/60 hover:text-white transition-colors text-sm mr-2"
              >
                <ArrowLeft size={16} /> Back
              </button>
            )}
            <div className="w-7 h-7 bg-gradient-to-br from-[#F76B1C] to-[#FF9A56] rounded-lg flex items-center justify-center">
              <Sparkles size={16} />
            </div>
            <div>
              <span className="font-bold text-sm">Resin Genius</span>
              <span className="text-white/50 text-xs ml-2">by 3D Printer Gear</span>
            </div>
          </div>
          <button
            onClick={handleClose}
            data-testid="resin-modal-close"
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden">
          {view === "home" && (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-gray-50">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 text-[#F76B1C] text-xs font-semibold mb-6 border border-orange-100">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F76B1C] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#F76B1C]" />
                </span>
                AI-Powered Resin Recommendation
              </div>

              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                Find Your Perfect <span className="text-[#F76B1C]">Formlabs Resin</span>
              </h2>
              <p className="max-w-lg text-gray-500 mb-8 text-sm leading-relaxed">
                Get instant, expert material advice tailored for your SLA printer. Choose how you'd like to search from the 45+ resin catalog.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-lg">
                <button
                  onClick={() => setView("chat")}
                  data-testid="resin-mode-chat"
                  className="flex flex-col items-start text-left bg-white p-5 rounded-xl border border-gray-200 hover:shadow-lg hover:border-[#F76B1C] transition-all group"
                >
                  <div className="w-10 h-10 bg-[#0A0A0A] text-white rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <MessageSquare size={20} />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">AI Chat</h3>
                  <p className="text-gray-500 text-xs mb-3 leading-relaxed">Describe your project and get expert advice instantly.</p>
                  <div className="mt-auto flex items-center gap-1 text-[#F76B1C] font-semibold text-sm group-hover:gap-2 transition-all">
                    Open Chat <ArrowRight size={16} />
                  </div>
                </button>

                <button
                  onClick={() => setView("questionnaire")}
                  data-testid="resin-mode-wizard"
                  className="flex flex-col items-start text-left bg-white p-5 rounded-xl border border-gray-200 hover:shadow-lg hover:border-[#F76B1C] transition-all group"
                >
                  <div className="w-10 h-10 bg-[#F76B1C] text-white rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <ClipboardList size={20} />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">Guided Wizard</h3>
                  <p className="text-gray-500 text-xs mb-3 leading-relaxed">Answer questions for a curated list of resins.</p>
                  <div className="mt-auto flex items-center gap-1 text-[#F76B1C] font-semibold text-sm group-hover:gap-2 transition-all">
                    Start Wizard <ArrowRight size={16} />
                  </div>
                </button>
              </div>
            </div>
          )}

          {view === "chat" && <ChatView />}
          {view === "questionnaire" && <QuestionnaireView />}
        </div>
      </div>
    </div>
  );
};
